import React from 'react';
import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';

export interface FieldBasis {
  flex: string;
  minWidth?: string;
}

export interface MappingFormFieldGridItemProps {
  children: React.ReactNode;
  flex?: string;
  minWidth?: string;
  'data-testid'?: string;
}

export interface MappingFormFieldGridProps {
  children: React.ReactNode;
  columns?: 1 | 2 | 3 | 4;
  basis?: FieldBasis;
  gap?: number;
  mb?: number;
  sx?: SxProps<Theme>;
}

const COLUMN_PRESETS: Record<number, FieldBasis> = {
  1: { flex: '1 1 100%' },
  2: { flex: '1 1 calc(50% - 8px)', minWidth: '280px' },
  3: { flex: '1 1 calc(33.33% - 11px)', minWidth: '220px' },
  4: { flex: '1 1 calc(25% - 12px)', minWidth: '200px' },
};

/**
 * Item wrapper that allows per-child overrides (e.g. data-testid) inside a
 * MappingFormFieldGrid. The grid still applies the column/basis layout.
 */
export const MappingFormFieldGridItem: React.FC<MappingFormFieldGridItemProps> = ({
  children,
}) => <>{children}</>;

MappingFormFieldGridItem.displayName = 'MappingFormFieldGridItem';

/**
 * Shared flex-grid wrapper for form fields in mapping section editors.
 *
 * Replaces the repeated Box + flex-basis recipe so each section editor only
 * declares its fields and column/basis layout.
 */
const MappingFormFieldGrid: React.FC<MappingFormFieldGridProps> & {
  Item: typeof MappingFormFieldGridItem;
} = ({ children, columns = 2, basis, gap = 0.5, mb, sx }) => {
  const preset = COLUMN_PRESETS[columns];
  const childBasis = basis ?? preset;

  return (
    <Box sx={[{ display: 'flex', flexWrap: 'wrap', gap, mb }, ...(Array.isArray(sx) ? sx : [sx])]}>
      {React.Children.map(children, (child, index) => {
        if (
          React.isValidElement(child) &&
          child.type === MappingFormFieldGridItem
        ) {
          const itemProps = child.props as MappingFormFieldGridItemProps;
          return (
            <Box
              key={index}
              data-testid={itemProps['data-testid']}
              sx={{
                flex: itemProps.flex ?? childBasis.flex,
                minWidth: itemProps.minWidth ?? childBasis.minWidth,
              }}
            >
              {itemProps.children}
            </Box>
          );
        }

        return (
          <Box
            key={index}
            sx={{ flex: childBasis.flex, minWidth: childBasis.minWidth }}
          >
            {child}
          </Box>
        );
      })}
    </Box>
  );
};

MappingFormFieldGrid.Item = MappingFormFieldGridItem;

export default MappingFormFieldGrid;
